<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Schedule Task</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Task Type Selection -->
          <div class="mb-4">
            <label class="form-label">Task Type <span class="text-danger">*</span></label>
            <div class="row g-2">
              <div v-for="type in taskTypes" :key="type.value" class="col-md-6">
                <div class="form-check card p-3"
                     :class="{'border-primary bg-light': form.activityType === type.value}"
                     @click="selectTaskType(type)">
                  <div class="d-flex align-items-center">
                    <div class="avatar-sm flex-shrink-0">
                      <div :class="'avatar-title rounded-circle text-white ' + type.color">
                        <i :class="'bx ' + type.icon"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h6 class="mb-0">{{ type.label }}</h6>
                      <small class="text-muted">{{ type.description }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Household Selection (if needed) -->
          <div v-if="requiresHousehold" class="mb-3">
            <label class="form-label">Select Household</label>
            <div class="input-group">
              <input v-model="householdSearch" @input="searchHouseholds" type="text"
                     class="form-control" placeholder="Search household or member name..."
                     :class="{'is-valid': selectedHousehold}">
              <button v-if="selectedHousehold" @click="clearHousehold"
                      class="btn btn-outline-secondary" type="button">
                <i class="bx bx-x"></i>
              </button>
            </div>

            <!-- Selected Household Display -->
            <div v-if="selectedHousehold" class="mt-2 p-2 bg-light rounded">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ selectedHousehold.household_number }}</strong>
                  <br>
                  <small class="text-muted">{{ selectedHousehold.household_head_name }}</small>
                </div>
                <span v-if="selectedHousehold.cbhi_member_count > 0" class="badge bg-success">
                  CBHI Active
                </span>
              </div>
            </div>

            <!-- Search Results Dropdown -->
            <div v-if="householdResults.length > 0 && !selectedHousehold"
                 class="dropdown-menu show w-100 mt-1" style="max-height: 200px; overflow-y: auto;">
              <a v-for="household in householdResults" :key="household.household_id"
                 class="dropdown-item" href="#" @click.prevent="selectHousehold(household)">
                <div class="d-flex justify-content-between">
                  <div>
                    <strong>{{ household.household_number }}</strong>
                    <br>
                    <small>{{ household.household_head_name }}</small>
                  </div>
                  <small class="text-muted">{{ household.member_count }} members</small>
                </div>
              </a>
            </div>
          </div>

          <!-- Schedule Details -->
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Scheduled Date <span class="text-danger">*</span></label>
              <input v-model="form.scheduledDate" type="date" class="form-control"
                     :min="minDate" required>
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label">Time (Optional)</label>
              <input v-model="form.scheduledTime" type="time" class="form-control">
            </div>
          </div>

          <!-- Priority -->
          <div class="mb-3">
            <label class="form-label">Priority</label>
            <div class="btn-group w-100" role="group">
              <input type="radio" class="btn-check" id="priority-low"
                     value="low" v-model="form.priority">
              <label class="btn btn-outline-primary" for="priority-low">
                <i class="bx bx-down-arrow-alt"></i> Low
              </label>

              <input type="radio" class="btn-check" id="priority-medium"
                     value="medium" v-model="form.priority">
              <label class="btn btn-outline-warning" for="priority-medium">
                <i class="bx bx-minus"></i> Medium
              </label>

              <input type="radio" class="btn-check" id="priority-high"
                     value="high" v-model="form.priority">
              <label class="btn btn-outline-danger" for="priority-high">
                <i class="bx bx-up-arrow-alt"></i> High
              </label>
            </div>
          </div>

          <!-- Activity Description -->
          <div class="mb-3">
            <label class="form-label">Activity Description <span class="text-danger">*</span></label>
            <textarea v-model="form.activityDescription" class="form-control" rows="3"
                      placeholder="Describe the task or activity..." required></textarea>
          </div>

          <!-- Additional Notes -->
          <div class="mb-3">
            <label class="form-label">Notes (Optional)</label>
            <textarea v-model="form.notes" class="form-control" rows="2"
                      placeholder="Any additional notes or reminders..."></textarea>
          </div>

          <!-- Recurring Task -->
          <div class="mb-3">
            <div class="form-check form-switch">
              <input v-model="form.recurring" class="form-check-input" type="checkbox"
                     id="recurringCheck">
              <label class="form-check-label" for="recurringCheck">
                Make this a recurring task
              </label>
            </div>
          </div>

          <!-- Recurrence Pattern (if recurring) -->
          <div v-if="form.recurring" class="mb-3">
            <label class="form-label">Recurrence Pattern</label>
            <select v-model="form.recurrencePattern" class="form-select">
              <option value="">Select pattern</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every 2 weeks</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <!-- Estimated Duration -->
          <div class="mb-3">
            <label class="form-label">Estimated Duration (minutes)</label>
            <input v-model.number="form.estimatedDuration" type="number"
                   class="form-control" min="15" step="15" placeholder="30">
          </div>

          <!-- Quick Templates -->
          <div v-if="templates.length > 0" class="mb-3">
            <label class="form-label">Quick Templates</label>
            <div class="d-flex flex-wrap gap-2">
              <button v-for="template in templates" :key="template.id"
                      @click="applyTemplate(template)" type="button"
                      class="btn btn-sm btn-outline-secondary">
                {{ template.name }}
              </button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>
          <button type="button" class="btn btn-primary" @click="scheduleTask"
                  :disabled="!isFormValid || submitting">
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Scheduling...
            </span>
            <span v-else>
              <i class="bx bx-calendar-plus"></i> Schedule Task
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ScheduleTaskModal",
  props: {
    preselectedHousehold: {
      type: Object,
      default: null
    },
    preselectedType: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      submitting: false,

      // Form data
      form: {
        activityType: this.preselectedType || '',
        activityDescription: '',
        scheduledDate: '',
        scheduledTime: '',
        priority: 'medium',
        notes: '',
        recurring: false,
        recurrencePattern: '',
        estimatedDuration: 30
      },

      // Household selection
      selectedHousehold: this.preselectedHousehold,
      householdSearch: '',
      householdResults: [],
      searchTimeout: null,

      // Task types
      taskTypes: [
        {
          value: 'routine_visit',
          label: 'Routine Visit',
          description: 'Regular household health check',
          icon: 'bx-home-circle',
          color: 'bg-primary',
          requiresHousehold: true
        },
        {
          value: 'follow_up',
          label: 'Follow-up Visit',
          description: 'Follow up on previous health issue',
          icon: 'bx-revision',
          color: 'bg-warning',
          requiresHousehold: true
        },
        {
          value: 'anc_visit',
          label: 'ANC Visit',
          description: 'Antenatal care checkup',
          icon: 'bx-female',
          color: 'bg-danger',
          requiresHousehold: true
        },
        {
          value: 'immunization',
          label: 'Immunization',
          description: 'Vaccination appointment',
          icon: 'bx-injection',
          color: 'bg-success',
          requiresHousehold: true
        },
        {
          value: 'health_education',
          label: 'Health Education',
          description: 'Community education session',
          icon: 'bx-book-open',
          color: 'bg-info',
          requiresHousehold: false
        },
        {
          value: 'environmental_assessment',
          label: 'Environmental Assessment',
          description: 'WASH and sanitation check',
          icon: 'bx-water',
          color: 'bg-secondary',
          requiresHousehold: true
        },
        {
          value: 'disease_surveillance',
          label: 'Disease Surveillance',
          description: 'Community disease monitoring',
          icon: 'bx-radar',
          color: 'bg-dark',
          requiresHousehold: false
        },
        {
          value: 'administrative',
          label: 'Administrative',
          description: 'Reports, meetings, etc.',
          icon: 'bx-briefcase',
          color: 'bg-secondary',
          requiresHousehold: false
        }
      ],

      // Templates for quick fill
      templates: [
        {
          id: 1,
          name: 'Monthly Household Visit',
          data: {
            activityType: 'routine_visit',
            activityDescription: 'Monthly routine household health promotion visit',
            priority: 'medium',
            recurring: true,
            recurrencePattern: 'monthly',
            estimatedDuration: 45
          }
        },
        {
          id: 2,
          name: 'Pregnancy Follow-up',
          data: {
            activityType: 'anc_visit',
            activityDescription: 'Antenatal care follow-up visit',
            priority: 'high',
            estimatedDuration: 60
          }
        },
        {
          id: 3,
          name: 'Vaccination Reminder',
          data: {
            activityType: 'immunization',
            activityDescription: 'Child immunization appointment',
            priority: 'high',
            estimatedDuration: 30
          }
        }
      ],

      minDate: new Date().toISOString().split('T')[0]
    };
  },

  computed: {
    requiresHousehold() {
      const taskType = this.taskTypes.find(t => t.value === this.form.activityType);
      return taskType ? taskType.requiresHousehold : false;
    },

    isFormValid() {
      return this.form.activityType &&
          this.form.activityDescription &&
          this.form.scheduledDate &&
          (!this.requiresHousehold || this.selectedHousehold) &&
          (!this.form.recurring || this.form.recurrencePattern);
    }
  },

  mounted() {
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.form.scheduledDate = tomorrow.toISOString().split('T')[0];

    // If household preselected, set the search
    if (this.preselectedHousehold) {
      this.householdSearch = this.preselectedHousehold.household_number;
    }
  },

  methods: {
    selectTaskType(type) {
      this.form.activityType = type.value;

      // Auto-fill description if empty
      if (!this.form.activityDescription) {
        this.form.activityDescription = type.description;
      }

      // Clear household if not required
      if (!type.requiresHousehold) {
        this.selectedHousehold = null;
        this.householdSearch = '';
      }
    },

    async searchHouseholds() {
      if (!this.requiresHousehold) return;

      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Clear selection if searching
      this.selectedHousehold = null;

      if (!this.householdSearch || this.householdSearch.length < 2) {
        this.householdResults = [];
        return;
      }

      // Debounce search
      this.searchTimeout = setTimeout(async () => {
        const data = {
          "function": "getHouseholds",
          "searchTerm": this.householdSearch,
          "limit": 10,
          "offset": 0
        };

        try {
          const res = await executeJson(data, authUrl);

          if (res.data.success) {
            this.householdResults = res.data.data.households || [];
          }
        } catch (e) {
          console.error("Failed to search households:", e);
        }
      }, 300);
    },

    selectHousehold(household) {
      this.selectedHousehold = household;
      this.householdSearch = household.household_number;
      this.householdResults = [];
    },

    clearHousehold() {
      this.selectedHousehold = null;
      this.householdSearch = '';
      this.householdResults = [];
    },

    applyTemplate(template) {
      // Apply template data to form
      Object.assign(this.form, template.data);

      // Select the task type
      const taskType = this.taskTypes.find(t => t.value === template.data.activityType);
      if (taskType) {
        this.selectTaskType(taskType);
      }
    },

    async scheduleTask() {
      if (!this.isFormValid) return;

      this.submitting = true;

      const data = {
        "function": "createWorkSchedule",
        "householdId": this.selectedHousehold?.household_id || null,
        "scheduledDate": this.form.scheduledDate,
        "scheduledTime": this.form.scheduledTime || null,
        "activityType": this.form.activityType,
        "activityDescription": this.form.activityDescription,
        "priority": this.form.priority,
        "estimatedDuration": this.form.estimatedDuration,
        "recurring": this.form.recurring,
        "recurrencePattern": this.form.recurring ? this.form.recurrencePattern : '',
        "notes": this.form.notes
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('created', res.data.data);
        } else {
          alert(res.data.message || 'Failed to schedule task');
        }
      } catch (e) {
        console.error("Failed to schedule task:", e);
        alert('Failed to schedule task. Please try again.');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.form-check.card {
  cursor: pointer;
  transition: all 0.2s;
}

.form-check.card:hover {
  border-color: #556ee6;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
}

.dropdown-menu {
  position: relative !important;
  transform: none !important;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.btn-check:checked + .btn-outline-primary {
  background-color: #556ee6;
  border-color: #556ee6;
  color: #fff;
}

.btn-check:checked + .btn-outline-warning {
  background-color: #f1b44c;
  border-color: #f1b44c;
  color: #fff;
}

.btn-check:checked + .btn-outline-danger {
  background-color: #f46a6a;
  border-color: #f46a6a;
  color: #fff;
}
</style>